// Thin GraphQL client for the admin portal. We don't reuse @api/appsync-client
// directly because it imports the main app's REST client.js; copying the
// ~40 lines we need keeps this app's dependency graph small and auditable.
import * as Sentry from '@sentry/react'

const APPSYNC_URL = import.meta.env.VITE_APPSYNC_URL

const EXPECTED_ERROR_TYPES = new Set([
  'MFA_REQUIRED',
  'ORG_NOT_FOUND',
])

let _getToken = async () => null
export function setTokenProvider(fn) { _getToken = fn }

export async function gql(query, variables = {}) {
  if (!APPSYNC_URL) throw new Error('VITE_APPSYNC_URL is not configured')
  const token = await _getToken()
  const res = await fetch(APPSYNC_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: token },
    body: JSON.stringify({ query, variables }),
  })
  const json = await res.json()
  if (json.errors?.length) {
    const e = json.errors[0]
    const code = e.errorType ?? 'APPSYNC_ERROR'
    if (!EXPECTED_ERROR_TYPES.has(code)) {
      Sentry.captureException(new Error(`AppSync error: ${code}`), {
        extra: { message: e.message, errorType: e.errorType },
      })
    }
    throw { message: e.message, code }
  }
  return json.data
}

// ── Queries ────────────────────────────────────────────────────────────────
export const listOrgs = () => gql(`
  query { listOrgs {
    orgId name slug maxUsers createdAt cognitoPoolId
    userCount activeAdmins pendingAdmins
  } }
`).then((d) => d.listOrgs)

export const getOrg = (orgId) => gql(`
  query Q($id: ID!) { getOrg(orgId: $id) {
    orgId name slug maxUsers createdAt cognitoPoolId userCount
    admins { userId email name role status }
  } }
`, { id: orgId }).then((d) => d.getOrg)

export const listAuditEvents = (orgId) => gql(`
  query Q($oid: ID) { listAuditEvents(orgId: $oid) {
    ts action orgId actor detail
  } }
`, { oid: orgId ?? null }).then((d) => d.listAuditEvents)

// ── Mutations ──────────────────────────────────────────────────────────────
export const createOrg = (input) => gql(`
  mutation M($input: CreateOrgInput!) { createOrg(input: $input) {
    orgId name slug
  } }
`, { input }).then((d) => d.createOrg)

export const inviteOrgAdmin = (orgId, input) => gql(`
  mutation M($id: ID!, $input: InviteOrgAdminInput!) {
    inviteOrgAdmin(orgId: $id, input: $input)
  }
`, { id: orgId, input })

export const resendOrgInvite = (orgId, input) => gql(`
  mutation M($id: ID!, $input: ResendOrgInviteInput!) {
    resendOrgInvite(orgId: $id, input: $input)
  }
`, { id: orgId, input })

export const revokeOrg = (orgId, input) => gql(`
  mutation M($id: ID!, $input: RevokeOrgInput!) {
    revokeOrg(orgId: $id, input: $input)
  }
`, { id: orgId, input })

const NOTIFICATION_FIELDS = `
  id type title body targetOrg targetRoles
  createdAt createdBy expiresAt
`

export const listAllNotifications = (targetOrg) => gql(`
  query Q($targetOrg: String) {
    listAllNotifications(targetOrg: $targetOrg) { ${NOTIFICATION_FIELDS} }
  }
`, { targetOrg: targetOrg ?? null }).then((d) => d.listAllNotifications)

export const publishNotification = (input) => gql(`
  mutation M($input: PublishNotificationInput!) {
    publishNotification(input: $input) { ${NOTIFICATION_FIELDS} }
  }
`, { input }).then((d) => d.publishNotification)

export const deleteNotification = (notificationId, targetOrg) => gql(`
  mutation M($notificationId: ID!, $targetOrg: String!) {
    deleteNotification(notificationId: $notificationId, targetOrg: $targetOrg)
  }
`, { notificationId, targetOrg })

export const getMe = () => gql(`
  query { getMe {
    userId email name isSuperAdmin isAdmin org
    appearance { theme mode }
  } }
`).then((d) => d.getMe)

export const listFeatures = () => gql(`
  query { listFeatures {
    flags  { key name description category editable attributes }
    values { key enabled attributes }
    lastDeployment { versionNumber state startedAt actor }
    devFlagsVersion
    orgFeaturesVersion
  } }
`).then((d) => d.listFeatures)

export const updateFeature = (featureKey, input) => gql(`
  mutation M($k: ID!, $input: UpdateFeatureInput!) {
    updateFeature(featureKey: $k, input: $input) { key enabled attributes }
  }
`, { k: featureKey, input }).then((d) => d.updateFeature)

export const getCostReport = (granularity, lookback) => gql(`
  query Q($g: String, $l: Int) { getCostReport(granularity: $g, lookback: $l) {
    env granularity lookback currency untaggedShare
    buckets { periodStart total services { name amount } }
  } }
`, { g: granularity, l: lookback ?? null }).then((d) => d.getCostReport)

export const getAnalytics = (period) => gql(`
  query Q($period: String) { getAnalytics(period: $period) {
    period
    kpis {
      totalOrgs totalUsers newOrgs newUsers
      prevTotalOrgs prevTotalUsers
      inviteAcceptanceRate prevInviteAcceptanceRate
      orphanedOrgs
    }
    inviteFunnel { sent accepted pending expiredOrRevoked }
    activationTime { medianHours under4h between4and48h over48h neverActivated }
    newOrgsPerMonth { month count }
    growthSeries { month orgs users }
    capUsage { orgId name used max pct }
  } }
`, { period }).then((d) => d.getAnalytics)
